/**
 * useTenants — data layer for the Monitored Tenants tab of the Admin Portal.
 *
 * Wraps the tenant-management + admin-consent API surface in React Query:
 *   - One list query (`getTenantManagementList`) — Guardian only renders one
 *     view of monitored tenants today, so the list key is parameterless.
 *   - One lazy per-tenant consent-status query (`getAdminConsentStatus`),
 *     enabled only when the drawer asks for a specific tenant. The legacy
 *     page (`TenantManagement.tsx`) bulk-loaded all consent statuses on
 *     mount; we deliberately invert that — the Admin Portal drawer fetches
 *     consent state per opened tenant, so the table render no longer waits
 *     on N round-trips.
 *   - Mutations for onboard / update / deactivate / test-auth / initiate-
 *     consent / verify-and-record-consent / revoke-consent / send-onboarding-
 *     email. Each invalidates `tenantKeys.all` on success; the consent-state
 *     mutations additionally invalidate the per-tenant consent key so the
 *     drawer reads fresh data without a manual refetch.
 *
 * Why no optimistic updates (unlike `useOrganizations`): the tenant table
 * isn't a queue — admin actions here are individually deliberate (open
 * drawer → act), so the cost of a refetch round-trip is invisible and the
 * server-derived state (consent valid? auth config valid?) is what the UI
 * actually keys off, not optimistic placeholders.
 */

import { useMemo } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'

import { api } from '@/lib/api'
import type {
  AdminConsentCallbackResponse,
  AdminConsentInitiateResponse,
  AdminConsentRevokeResponse,
  AdminConsentStatus,
  SendOnboardingEmailRequest,
  SendOnboardingEmailResponse,
} from '@/lib/api'

/* -------------------------------------------------------------------------- */
/* Query-key factory                                                          */
/* -------------------------------------------------------------------------- */

/**
 * Keys exported so callers (the nav badge, the tab, the drawer) can read or
 * invalidate the cache without re-deriving the key shape. Matches the
 * `orgKeys` convention in `useOrganizations.ts`; the consent-status branch
 * is unique to tenants because it's the one piece of per-row remote state
 * that needs targeted invalidation after `verify` / `revoke`.
 */
export const tenantKeys = {
  all: ['tenants'] as const,
  list: () => [...tenantKeys.all, 'list'] as const,
  detail: (id: string) => [...tenantKeys.all, 'detail', id] as const,
  consentStatus: (id: string) => [...tenantKeys.all, 'consent', id] as const,
}

/* -------------------------------------------------------------------------- */
/* Public types                                                               */
/* -------------------------------------------------------------------------- */

/**
 * Tenant row as returned by `/tenantmanagement` (Container endpoint,
 * proxied via the Function App). Mirrors the inline `Tenant` interface in
 * the legacy `TenantManagement.tsx:7-24` so the Admin Portal tab can
 * consume the hook without re-deriving the shape.
 *
 * NOTE: `lastScanAt` is a string in the wire format; the legacy page
 * `new Date()`s it on render, so callers should expect raw ISO-8601 here.
 */
export interface TenantDto {
  id: string
  tenantId: string
  name: string
  domain?: string
  onboardingStatus?: string
  notificationEmail?: string
  autoTaskEmail?: string
  rejectedDriftNotificationEmail?: string
  driftAlertNotificationEmail?: string
  isActive: boolean
  createdAt: string
  lastScanAt: string
  // Optional: only the list endpoint (`getTenantManagementList`) returns
  // this; the onboard/update/test-auth mutation results (`TenantDetail` in
  // `lib/api/tenants.ts`) carry `authConfigValid` instead. Kept optional
  // here so `TenantDto` can describe both shapes without lying about the
  // mutation results.
  hasAuthConfig?: boolean
  authenticationMode?: 'PerTenant' | 'MultiTenant'
  adminConsentGrantedAt?: string
  adminConsentGrantedBy?: string
}

/**
 * Payload shape for `onboardTenant` / `updateTenant`. Kept loose (matches
 * the legacy page's `TenantFormData`) because the underlying api.ts
 * methods accept `any` — locking this down would require widening the
 * api.ts signatures, which is out of scope for Slice C-hook.
 */
export interface TenantFormPayload {
  tenantId: string
  name: string
  domain?: string
  authenticationMode: 'PerTenant' | 'MultiTenant'
  clientId?: string
  certificateThumbprint?: string
  notificationEmail?: string
  autoTaskEmail?: string
  rejectedDriftNotificationEmail?: string
  driftAlertNotificationEmail?: string
}

export interface UseTenantsResult {
  tenants: TenantDto[]
  isLoading: boolean
  isError: boolean
  error?: Error
  refetch: () => void
  /**
   * Count of MultiTenant tenants that haven't yet recorded admin consent.
   * Drives the "Needs consent" filter chip and the verdict-line in the
   * MonitoredTenants score box. Derived locally — no extra round-trip.
   */
  pendingConsentCount: number

  onboardTenant: (data: TenantFormPayload) => Promise<TenantDto>
  isOnboarding: boolean
  updateTenant: (args: { tenantId: string; data: TenantFormPayload }) => Promise<TenantDto>
  isUpdating: boolean
  deactivateTenant: (tenantId: string) => Promise<void>
  isDeactivating: boolean
  testTenantAuth: (tenantId: string) => Promise<{ success: boolean; message: string }>
  isTestingAuth: boolean
  initiateAdminConsent: (tenantId: string) => Promise<AdminConsentInitiateResponse>
  isInitiatingConsent: boolean
  verifyAndRecordConsent: (args: { tenantId: string }) => Promise<AdminConsentCallbackResponse>
  isVerifyingConsent: boolean
  revokeAdminConsent: (tenantId: string) => Promise<AdminConsentRevokeResponse>
  isRevokingConsent: boolean
  sendOnboardingEmail: (args: {
    tenantId: string
    request: SendOnboardingEmailRequest
  }) => Promise<SendOnboardingEmailResponse>
  isSendingOnboardingEmail: boolean
}

/* -------------------------------------------------------------------------- */
/* Hook                                                                       */
/* -------------------------------------------------------------------------- */

const TENANT_STALE_MS = 30 * 1000

export function useTenants(): UseTenantsResult {
  const queryClient = useQueryClient()
  const listKey = tenantKeys.list()

  const listQuery = useQuery({
    queryKey: listKey,
    queryFn: async () => {
      // `api.getTenantManagementList()` returns `any[]` today — we narrow
      // here at the boundary so the rest of the app gets a typed array.
      const rows = await api.getTenantManagementList()
      return rows as TenantDto[]
    },
    staleTime: TENANT_STALE_MS,
  })

  const tenants = listQuery.data ?? []

  // Pending-consent count drives the FilterChipRow badge + the score-box
  // verdict-line in MonitoredTenantsTab. Pulled out as a pure helper so the
  // tests can exercise the derivation without standing up React Query.
  const pendingConsentCount = useMemo(
    () => derivePendingConsentCount(listQuery.data),
    [listQuery.data],
  )

  /* ---------- Mutations ---------- */

  // Each mutation invalidates `tenantKeys.all` on success so the list and
  // any open detail/consent queries refetch. The consent-state mutations
  // ALSO invalidate the per-tenant consent key — the drawer subscribes to
  // that key, and `tenantKeys.all` is a prefix of `consentStatus(id)`
  // (`['tenants', 'consent', id]`) so the prefix-match invalidation
  // naturally cascades, but we make the cascade explicit for readers.

  const onboardMutation = useMutation({
    mutationFn: (data: TenantFormPayload) =>
      api.onboardTenant(data) as Promise<TenantDto>,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: tenantKeys.all })
    },
  })

  const updateMutation = useMutation({
    mutationFn: ({ tenantId, data }: { tenantId: string; data: TenantFormPayload }) =>
      api.updateTenant(tenantId, data) as Promise<TenantDto>,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: tenantKeys.all })
    },
  })

  const deactivateMutation = useMutation({
    mutationFn: (tenantId: string) => api.deactivateTenant(tenantId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: tenantKeys.all })
    },
  })

  const testAuthMutation = useMutation({
    mutationFn: (tenantId: string) =>
      api.testTenantAuth(tenantId) as Promise<{ success: boolean; message: string }>,
    // No invalidation — test-auth is read-only against the live tenant
    // and never changes Guardian-side state.
  })

  const initiateConsentMutation = useMutation({
    mutationFn: (tenantId: string) => api.initiateAdminConsent(tenantId),
    // Initiate just returns a URL; the actual consent flip happens when
    // the customer's Global Admin completes consent + the callback fires.
    // Nothing to invalidate here.
  })

  const verifyConsentMutation = useMutation({
    mutationFn: ({ tenantId }: { tenantId: string }) => api.verifyAndRecordConsent(tenantId),
    onSuccess: (_data, { tenantId }) => {
      queryClient.invalidateQueries({ queryKey: tenantKeys.all })
      queryClient.invalidateQueries({ queryKey: tenantKeys.consentStatus(tenantId) })
    },
  })

  const revokeConsentMutation = useMutation({
    mutationFn: (tenantId: string) => api.revokeAdminConsent(tenantId),
    onSuccess: (_data, tenantId) => {
      queryClient.invalidateQueries({ queryKey: tenantKeys.all })
      queryClient.invalidateQueries({ queryKey: tenantKeys.consentStatus(tenantId) })
    },
  })

  const sendOnboardingEmailMutation = useMutation({
    mutationFn: ({ tenantId, request }: { tenantId: string; request: SendOnboardingEmailRequest }) =>
      api.sendOnboardingEmail(tenantId, request),
    // No invalidation — sending the email doesn't mutate any cached row.
  })

  return {
    tenants,
    isLoading: listQuery.isLoading,
    isError: listQuery.isError,
    error: listQuery.error as Error | undefined,
    refetch: () => { void listQuery.refetch() },
    pendingConsentCount,

    onboardTenant: (data) => onboardMutation.mutateAsync(data),
    isOnboarding: onboardMutation.isPending,
    updateTenant: (args) => updateMutation.mutateAsync(args),
    isUpdating: updateMutation.isPending,
    deactivateTenant: (tenantId) =>
      deactivateMutation.mutateAsync(tenantId).then(() => undefined),
    isDeactivating: deactivateMutation.isPending,
    testTenantAuth: (tenantId) => testAuthMutation.mutateAsync(tenantId),
    isTestingAuth: testAuthMutation.isPending,
    initiateAdminConsent: (tenantId) => initiateConsentMutation.mutateAsync(tenantId),
    isInitiatingConsent: initiateConsentMutation.isPending,
    verifyAndRecordConsent: (args) => verifyConsentMutation.mutateAsync(args),
    isVerifyingConsent: verifyConsentMutation.isPending,
    revokeAdminConsent: (tenantId) => revokeConsentMutation.mutateAsync(tenantId),
    isRevokingConsent: revokeConsentMutation.isPending,
    sendOnboardingEmail: (args) => sendOnboardingEmailMutation.mutateAsync(args),
    isSendingOnboardingEmail: sendOnboardingEmailMutation.isPending,
  }
}

/* -------------------------------------------------------------------------- */
/* Lazy per-tenant consent loader                                             */
/* -------------------------------------------------------------------------- */

export interface UseTenantConsentStatusResult {
  data: AdminConsentStatus | undefined
  isLoading: boolean
  isError: boolean
  refetch: () => void
}

/**
 * Lazily fetch the admin-consent status for a single tenant. Only enabled
 * when `tenantId` is non-null, so passing `null` (e.g. drawer closed)
 * leaves the query idle. Used by `TenantDrawerContent` to surface
 * consent state without forcing the parent tab to bulk-load N statuses
 * the way the legacy page did.
 */
export function useTenantConsentStatus(tenantId: string | null): UseTenantConsentStatusResult {
  const query = useQuery({
    queryKey: tenantId
      ? tenantKeys.consentStatus(tenantId)
      // Sentinel key when disabled — TanStack still needs a stable key,
      // but with `enabled: false` the queryFn never runs.
      : [...tenantKeys.all, 'consent', 'disabled'] as const,
    queryFn: () => api.getAdminConsentStatus(tenantId as string),
    enabled: tenantId !== null,
    staleTime: TENANT_STALE_MS,
  })

  return {
    data: query.data,
    isLoading: query.isLoading,
    isError: query.isError,
    refetch: () => { void query.refetch() },
  }
}

/* -------------------------------------------------------------------------- */
/* Pure helpers — exported for unit tests                                     */
/* -------------------------------------------------------------------------- */

/**
 * Count MultiTenant tenants that don't have a recorded admin-consent
 * grant yet. Drives the "Needs consent" filter chip + the score-box
 * verdict on the MonitoredTenants tab. Per-Tenant tenants are excluded
 * because they don't use admin consent at all (legacy cert-based auth).
 *
 * Pulled out so the tests can pin the behavior without invoking React.
 */
export function derivePendingConsentCount(tenants?: readonly TenantDto[]): number {
  if (!tenants || tenants.length === 0) return 0
  return tenants.filter(
    t => t.authenticationMode === 'MultiTenant' && !t.adminConsentGrantedAt,
  ).length
}
